import React, { useEffect, useState } from 'react';
import { Trash2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SearchableSelect from './SearchableSelect';
import InlinePriceEditor from './InlinePriceEditor';
import AddNewItemModal from '@/components/shared/AddNewItemModal';
import { needsLiterConversion, formatGramMl, mlToGram } from '@/lib/unitConverter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtDes = (n, d = 2) => parseFloat(n.toFixed(d));
const toTitleCase = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

const safeFloat = (val) => {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const parsed = parseFloat(String(val).replace(/,/g, '.'));
  return isNaN(parsed) ? 0 : parsed;
};

// Harga per ml (untuk info display dan HPP bahan non-liter)
const getHargaPerMl = (bahan) => {
  if (!bahan) return 0;
  const satuan = bahan.satuan ?? 'kg';
  const harga = bahan.harga_rupiah ?? 0;
  const hSatuan = (satuan || 'kg').toLowerCase();
  if (hSatuan === 'kg' || hSatuan === 'liter') return harga / 1000;
  if (hSatuan === 'gram' || hSatuan === 'ml') return harga;
  return harga / 1000;
};

// Harga per gram:
// Alkohol/liter: harga/gram = harga_liter / 1000 (harga per liter dibagi 1000 unit gram)
// Bibit/kg: harga/gram = harga_kg / 1000
// Catatan: konversi ml→gram (÷0.8) hanya untuk menghitung BERAT yang dibutuhkan, bukan untuk harga
const getHargaPerGram = (bahan) => {
  if (!bahan) return 0;
  const satuan = bahan.satuan ?? 'kg';
  const harga = bahan.harga_rupiah ?? 0;
  if (satuan === 'liter' || satuan === 'kg') return harga / 1000;
  if (satuan === 'gram') return harga;
  return harga / 1000;
};

export default function RacikanRow({ row, index, bahanList, onUpdate, onRemove, ukuranMl, hidePrices = false, readOnly = false }) {
  const kategoriOptions = ['Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'];
  const PARFAROME_GRADE = ['Deluxe', 'Premium', 'Exclusive'];
  const isParfarome = (row.vendor || '').toLowerCase().includes('parfarome');
  const [addModal, setAddModal] = useState(null); // { initialName, initialVendor }
  const isClientTab = row.tab === 'client' || row.dari_client;
  const isManualClient = row.dari_client === true;

  // Filter bahan sesuai kategori
  const bahanByKategori = bahanList.filter(b =>
    (b.kategori ?? '').trim().toLowerCase() === (row.kategori ?? '').trim().toLowerCase()
  );

  // Vendor unik untuk kategori yang dipilih
  const vendorList = [...new Set(
    bahanByKategori
      .filter(b => b.vendor && b.vendor.trim() !== '')
      .map(b => b.vendor.trim())
  )];

  const isBibit = (row.kategori ?? '').trim().toLowerCase() === 'bibit';

  // Bibit: wajib pilih vendor dulu → filter bahan berdasarkan vendor
  // Parfarome: juga filter berdasarkan grade setelah vendor dipilih
  const filteredBahan = bahanByKategori.filter(b => {
    if (b.dari_client) return false; // satala tab only
    const vendorMatch = row.vendor && row.vendor.trim() !== ''
      ? (b.vendor ?? '').trim().toLowerCase() === row.vendor.trim().toLowerCase()
      : true;
    const gradeMatch = isParfarome && row.grade
      ? (b.grade ?? '').trim() === row.grade.trim()
      : true;
    return vendorMatch && gradeMatch;
  });

  // Bibit: disable pilih bahan sebelum vendor dipilih
  // Parfarome: disable bahan sebelum grade dipilih
  const bahanDisabled = isBibit && (!row.vendor || row.vendor.trim() === '')
    || (isParfarome && (!row.grade || row.grade.trim() === ''));

  // Handler pergantian kategori dengan auto-select vendor & item jika hanya ada 1
  const handleKategoriChange = (newKategori) => {
    const matchingBahan = (bahanList || []).filter(b =>
      !b.dari_client &&
      (b.kategori ?? '').trim().toLowerCase() === (newKategori ?? '').trim().toLowerCase()
    );

    const matchingVendors = [...new Set(
      matchingBahan
        .filter(b => b.vendor && b.vendor.trim() !== '')
        .map(b => b.vendor.trim())
    )];

    let autoVendor = '';
    if (matchingVendors.length === 1) {
      autoVendor = matchingVendors[0];
    }

    const itemsForVendor = matchingBahan.filter(b => {
      if (autoVendor) return (b.vendor ?? '').trim().toLowerCase() === autoVendor.trim().toLowerCase();
      return true;
    });

    let autoBahanId = null;
    let autoBahanNama = '';
    if (itemsForVendor.length === 1) {
      autoBahanId = itemsForVendor[0].id;
      autoBahanNama = itemsForVendor[0].nama;
      if (!autoVendor && itemsForVendor[0].vendor) {
        autoVendor = itemsForVendor[0].vendor;
      }
    }

    onUpdate(index, {
      kategori: newKategori,
      vendor: autoVendor,
      bahan_id: autoBahanId,
      bahan_nama: autoBahanNama,
      grade: ''
    });
  };

  // Handler pergantian vendor dengan auto-select item jika hanya ada 1 item dari vendor tersebut
  const handleVendorChange = (newVendor) => {
    const itemsForVendor = bahanByKategori.filter(b => {
      if (!b || b.dari_client) return false;
      if (newVendor && newVendor.trim() !== '') {
        return (b.vendor ?? '').trim().toLowerCase() === newVendor.trim().toLowerCase();
      }
      return true;
    });

    let autoBahanId = null;
    let autoBahanNama = '';
    if (itemsForVendor.length === 1) {
      autoBahanId = itemsForVendor[0].id;
      autoBahanNama = itemsForVendor[0].nama;
    }

    onUpdate(index, {
      vendor: newVendor,
      bahan_id: autoBahanId,
      bahan_nama: autoBahanNama,
      grade: ''
    });
  };

  // Auto-select vendor if there is only 1 option available
  useEffect(() => {
    if (isClientTab) return;
    if (vendorList.length === 1 && (!row.vendor || row.vendor.trim() === '')) {
      onUpdate(index, { vendor: vendorList[0] });
    }
  }, [vendorList.join(','), row.vendor, isClientTab, index]);

  // Auto-select bahan if there is only 1 option available (and it's not disabled)
  useEffect(() => {
    if (isClientTab) return;
    if (!bahanDisabled && filteredBahan.length === 1 && !row.bahan_id) {
      const singleItem = filteredBahan[0];
      onUpdate(index, { 
        bahan_id: singleItem.id, 
        bahan_nama: singleItem.nama,
        vendor: row.vendor || (singleItem.vendor ?? '')
      });
    }
  }, [filteredBahan.map(b => b.id).join(','), row.bahan_id, row.vendor, bahanDisabled, isClientTab, index]);

  const selectedBahan = bahanList.find(b => b.id === row.bahan_id);
  const persentaseFloat = safeFloat(row.persentase);
  const volumeMl = ukuranMl > 0 && persentaseFloat > 0 ? (persentaseFloat / 100) * ukuranMl : 0;

  // Default harga jual = harga_jual (DB) atau harga beli * 1.3 jika belum ada
  const hJual = (row.harga_jual_satuan !== undefined && row.harga_jual_satuan !== 0) 
    ? row.harga_jual_satuan 
    : (selectedBahan?.harga_jual || (selectedBahan?.harga_rupiah ? selectedBahan.harga_rupiah * 1.3 : 0));

  const hSatuanBahan = (selectedBahan?.satuan || 'kg').toLowerCase();
  // Harga per satuan terkecil (per ml / per gram) dihitung berdasarkan HARGA JUAL (Request User)
  // If user manually set a custom per-gram/ml price, use that; otherwise derive from satuan price
  const hargaJualPerKecil = row.custom_harga_per_unit != null && row.custom_harga_per_unit !== 0
    ? row.custom_harga_per_unit
    : (hSatuanBahan === 'gram') ? hJual : hJual / 1000;

  // Alkohol/Aqua Des: HPP dihitung dari gram (bukan ml), tampilkan gram
  const isLiterBahan = needsLiterConversion(selectedBahan?.satuan, selectedBahan?.kategori);
  const volumeGram = isLiterBahan && volumeMl > 0 ? volumeMl * 0.8 : 0; // ml * 0.8 = gram

  // Menghitung HPP Jual
  const hppBahanRaw = volumeMl > 0 ? hargaJualPerKecil * volumeMl : 0;
  const hppBahan = isManualClient ? 0 : hppBahanRaw; // Total Harga Jual

  // Menghitung HPP Beli (Modal Asli)
  const hargaBeliSatuan = selectedBahan?.harga_rupiah ?? 0;
  const hargaBeliPerKecil = (hSatuanBahan === 'gram') ? hargaBeliSatuan : hargaBeliSatuan / 1000;
  const hppBeliRaw = volumeMl > 0 ? hargaBeliPerKecil * volumeMl : 0;
  const hppBeli = isManualClient ? 0 : hppBeliRaw; // Total Modal Beli

  useEffect(() => {
    onUpdate(index, {
      volume_ml: volumeMl,
      volume_gram: isLiterBahan ? volumeGram : null,
      hpp_bahan: hppBahan,
      harga_per_satuan: selectedBahan?.harga_rupiah ?? 0,
      harga_jual_satuan: hJual,
      satuan: isLiterBahan ? 'g' : (selectedBahan?.satuan ?? 'kg'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volumeMl, volumeGram, hppBahan, selectedBahan?.harga_rupiah, hJual, isLiterBahan, selectedBahan?.satuan, index]);

  const handleAddNew = (searchText) => {
    setAddModal({ initialName: searchText || '', initialVendor: row.vendor || '' });
  };

  const handleCreated = (newItem) => {
    setAddModal(null);
    onUpdate(index, { bahan_id: newItem.id, bahan_nama: newItem.nama, vendor: row.vendor || (newItem.vendor ?? '') });
  };

  return (
    <>
    {addModal && (
      <AddNewItemModal
        type="bahan"
        initialName={addModal.initialName}
        initialVendor={addModal.initialVendor}
        initialKategori={row.kategori || 'Bibit'}
        onClose={() => setAddModal(null)}
        onCreated={handleCreated}
      />
    )}
    <div className={`py-2.5 border-b border-border/50 last:border-0 space-y-1.5 ${isClientTab ? 'bg-[#F9F5EC]/30 rounded-xl px-2 -mx-2' : ''}`}>
      <div className="grid grid-cols-12 gap-2 items-start">
        {/* Kategori */}
        <div className="col-span-2">
          <Select disabled={readOnly} value={row.kategori || 'Bibit'} onValueChange={handleKategoriChange}>
            <SelectTrigger className="h-8 text-xs border-border bg-background mb-1.5 focus:ring-1 focus:ring-accent/50 w-full disabled:opacity-50">
              <SelectValue placeholder="Pilih Kategori" />
            </SelectTrigger>
            <SelectContent>
              {kategoriOptions.map(k => <SelectItem key={k} value={k} className="text-xs">{k}</SelectItem>)}
            </SelectContent>
          </Select>
          {!readOnly && (
            <div className="flex bg-slate-100 p-0.5 rounded-md w-full">
              <button type="button" onClick={() => onUpdate(index, { tab: 'satala', dari_client: false, bahan_id: null, vendor: '', grade: '' })} className={`flex-1 text-[9px] font-bold py-1 rounded-sm transition-all ${!isClientTab ? 'bg-white text-[#8C6208] shadow-sm' : 'text-slate-500'}`}>Satala</button>
              <button type="button" onClick={() => onUpdate(index, { tab: 'client', dari_client: true, bahan_id: null, bahan_nama: `${toTitleCase(row.kategori)} Dari Client`, vendor: 'Client', grade: '' })} className={`flex-1 text-[9px] font-bold py-1 rounded-sm transition-all ${isClientTab ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500'}`}>Dari Client</button>
            </div>
          )}
        </div>

        {/* Vendor + Bahan (atau Dari Client input) */}
        {isClientTab ? (
          <div className="col-span-5 space-y-2">
            <div>
              <SearchableSelect
                disabled={readOnly}
                options={bahanByKategori.filter(b => b.dari_client)}
                value={row.bahan_id}
                onChange={v => {
                  const b = bahanList.find(x => x.id === v);
                  onUpdate(index, { bahan_id: v, bahan_nama: b?.nama ?? '', vendor: b?.vendor ?? 'Client', dari_client: false, harga_jual_satuan: 0, custom_harga_per_unit: 0 });
                }}
                placeholder="Pilih dari inventori client..."
                displayKey="nama"
                valueKey="id"
                renderOption={(opt) => (
                  <div>
                    <div className="font-medium text-sm">{opt.nama}</div>
                    {!hidePrices && <div className="text-xs text-muted-foreground mt-0.5">Rp {fmt(opt.harga_rupiah)}/{opt.satuan || 'kg'}</div>}
                  </div>
                )}
              />
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
              <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
            </div>
            
            <div className="flex items-center gap-2 bg-[#F9F5EC] border border-[#E6D5B8] rounded-lg px-2 py-1">
              <Package className="w-3.5 h-3.5 text-[#8C6208] shrink-0" />
              <Input
                disabled={readOnly}
                value={row.bahan_nama || ''}
                onChange={e => onUpdate(index, { bahan_nama: e.target.value, dari_client: !!e.target.value, bahan_id: null, vendor: 'Client' })}
                placeholder="Ketik manual nama bahan client..."
                className="h-7 text-xs border-0 bg-transparent focus-visible:ring-0 px-1 placeholder:text-[#A39171] text-[#302621] font-medium w-full"
              />
            </div>
          </div>
        ) : (
          <>
            {/* Vendor */}
            <div className="col-span-2">
              {vendorList.length > 0 ? (
                <SearchableSelect
                  disabled={readOnly}
                  options={vendorList}
                  value={row.vendor}
                  onChange={handleVendorChange}
                  placeholder={isBibit ? 'Pilih vendor *' : 'Semua vendor'}
                />
              ) : (
                <span className="text-xs text-muted-foreground px-2 italic">— Tidak ada vendor</span>
              )}
            </div>

            {/* Nama Bahan */}
            <div className="col-span-3">
              {bahanDisabled ? (
                <div className="w-full flex items-center px-3 py-2 text-xs border border-dashed border-border rounded-lg text-muted-foreground bg-muted/30 cursor-not-allowed">
                  {!row.vendor ? '← Pilih vendor dulu' : isParfarome && !row.grade ? '← Pilih grade dulu' : '← Pilih vendor dulu'}
                </div>
              ) : (
                <SearchableSelect
                  disabled={readOnly}
                  options={filteredBahan}
                  value={row.bahan_id}
                  onChange={v => {
                    const b = bahanList.find(x => x.id === v);
                    onUpdate(index, { bahan_id: v, bahan_nama: b?.nama ?? '', vendor: row.vendor || (b?.vendor ?? ''), harga_jual_satuan: 0, custom_harga_per_unit: 0 });
                  }}
                  placeholder="Pilih bahan"
                  displayKey="nama"
                  valueKey="id"
                  onAddNew={!readOnly ? handleAddNew : undefined}
                  renderOption={(opt) => {
                    return (
                      <div>
                        <div className="font-medium text-sm">{opt.nama}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {opt.vendor && <span className="text-xs text-muted-foreground">{opt.vendor}</span>}
                          {!hidePrices && (
                            opt.harga_rupiah > 0 ? (
                              <span className="text-xs font-semibold text-accent">Rp {fmt(opt.harga_rupiah)}/{opt.satuan || 'kg'}</span>
                            ) : (
                              <span className="text-xs text-amber-600 font-medium">Harga belum diisi</span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
              )}
            </div>
          </>
        )}

        {/* Persentase */}
        <div className="col-span-2">
          <div className="relative">
            <Input
              disabled={readOnly}
              type="text"
              value={row.persentase === 0 ? '' : row.persentase}
              onChange={e => {
                onUpdate(index, { persentase: e.target.value });
              }}
              className="pr-6 text-sm h-8"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
          </div>
        </div>

        {/* Volume */}
        <div className="col-span-2 text-center">
          {volumeMl > 0 ? (
            isLiterBahan ? (
              <span className="text-xs font-semibold text-amber-800 leading-tight">
                {formatGramMl(volumeMl, 100)?.label || `${volumeGram.toFixed(2)}g/${volumeMl.toFixed(2)}ml`}
              </span>
            ) : (
              <span className="text-xs font-medium text-accent">
                {volumeMl >= 100 ? `${(volumeMl / 1000).toFixed(2)} L` : `${volumeMl.toFixed(2)} ml`}
              </span>
            )
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        {/* Hapus */}
        {!readOnly && (
          <div className="col-span-1 flex justify-end">
            <Button variant="ghost" size="icon" className="w-7 h-7 text-destructive/60 hover:text-destructive" onClick={() => onRemove(index)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Info bahan terpilih: harga/satuan + harga turunan + HPP bahan */}
      {selectedBahan && (
        <div className="ml-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground pl-1 mt-1">
          <span className="font-medium text-foreground">{selectedBahan.nama}</span>
          {isManualClient ? (
            <span className="bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1">
              <Package className="w-3 h-3" /> Dari Client {!hidePrices && '— Modal = Rp 0'}
            </span>
          ) : (
            !hidePrices && (
              <>
                <span className="text-muted-foreground/70">
                  (Beli: Rp {fmt(selectedBahan.harga_rupiah)}/{selectedBahan.satuan})
                </span>
                
                <span className="text-muted-foreground/40">|</span>
                
                {/* Editable Harga Jual Satuan */}
                <div className="flex items-center gap-1.5">
                  <InlinePriceEditor 
                    disabled={readOnly}
                    label="Harga Jual:"
                    value={row.harga_jual_satuan}
                    onSave={(val) => onUpdate(index, { harga_jual_satuan: val })}
                    suffix={`/${selectedBahan.satuan || 'kg'}`}
                  />
                </div>

                <span className="text-muted-foreground/60">→</span>
                
                {isLiterBahan ? (
                  <>
                    <InlinePriceEditor 
                      disabled={readOnly}
                      label=""
                      value={hargaJualPerKecil}
                      onSave={(val) => onUpdate(index, { custom_harga_per_unit: val })}
                      suffix="/g"
                    />
                  </>
                ) : (
                  <InlinePriceEditor 
                    disabled={readOnly}
                    label=""
                    value={hargaJualPerKecil}
                    onSave={(val) => onUpdate(index, { custom_harga_per_unit: val })}
                    suffix="/g"
                  />
                )}
                
                <span className="text-muted-foreground/60">→</span>

                <div className="flex gap-1.5 items-center bg-slate-50 border border-slate-100 rounded-md px-2 py-0.5 ml-1">
                  {hppBeli > 0 && (
                    <span className="text-slate-600 font-medium">
                      Modal: Rp {fmt(hppBeli)}
                    </span>
                  )}
                  {hppBeli > 0 && hppBahan > 0 && <span className="text-slate-300">|</span>}
                  {hppBahan > 0 && (
                    <span className="text-emerald-700 font-bold">
                      Jual: Rp {fmt(hppBahan)}
                    </span>
                  )}
                </div>
              </>
            )
          )}
        </div>
      )}

      {/* Peringatan bibit belum pilih vendor */}
      {isBibit && !row.vendor && (
        <p className="text-[10px] text-amber-600 pl-1">⚠ Pilih vendor terlebih dahulu untuk melihat daftar bibit</p>
      )}

      {/* Grade selector khusus Parfarome — muncul setelah vendor dipilih, sebelum bahan */}
      {isBibit && isParfarome && row.vendor && (
        <div className="pl-1 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-amber-600 font-semibold">Grade:</span>
          <div className="flex gap-1.5">
            {PARFAROME_GRADE.map(g => (
              <button
                key={g}
                type="button"
                onClick={() => onUpdate(index, { grade: g, bahan_id: null, bahan_nama: '' })}
                className={`px-3 py-1 rounded-lg border text-xs font-semibold transition-all ${row.grade === g ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-300 text-amber-700 hover:border-amber-500'}`}
              >
                {g}
              </button>
            ))}
          </div>
          {!row.grade && <p className="text-[10px] text-red-500">⚠ Wajib pilih grade</p>}
        </div>
      )}
    </div>
    </>
  );
}